import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ChatContainer = styled(motion.div)`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
`;

const ChatHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
`;

const ChatTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled(motion.button)`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 107, 53, 0.5);
    border-radius: 2px;
  }
`;

const MessageBubble = styled(motion.div)`
  max-width: 85%;
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  background: ${props => props.isOwn ? 
    'linear-gradient(135deg, #FF6B35, #F7931E)' : 
    'rgba(255, 255, 255, 0.1)'
  };
  color: white;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.isOwn ? 
    '18px 18px 4px 18px' : 
    '18px 18px 18px 4px'
  };
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const MessageHeader = styled.div`
  font-size: 0.7rem;
  opacity: 0.8;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const MessageContent = styled.div`
  font-size: 0.9rem;
  line-height: 1.4;
`;

const MessageTime = styled.div`
  font-size: 0.6rem;
  opacity: 0.6;
  margin-top: 0.25rem;
  text-align: ${props => props.isOwn ? 'right' : 'left'};
`;

const SystemMessage = styled(motion.div)`
  align-self: center;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-style: italic;
  text-align: center;
  margin: 0.25rem 0;
`;

const InputContainer = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  padding: 0.75rem;
  font-size: 0.9rem;
  resize: none;
  max-height: 80px;
  min-height: 40px;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const SendButton = styled(motion.button)`
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  color: white;
  padding: 0.75rem;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }
  
  &:not(:disabled):hover {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
`;

const OnlineIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
`;

const OnlineDot = styled.div`
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
`;

function ChatPanel({ messages, onSendMessage, onClose, participants }) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 80) + 'px';
    }
  }, [inputValue]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      onSendMessage({
        id: Date.now(),
        text: inputValue.trim(),
        timestamp: new Date(),
        sender: {
          id: 'local',
          name: 'Tu'
        },
        type: 'text'
      });
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessage = (message) => {
    const isOwn = message.sender.id === 'local';
    const isSystem = message.type === 'system';

    if (isSystem) {
      return (
        <SystemMessage
          key={message.id}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          {message.text}
        </SystemMessage>
      );
    }

    return (
      <MessageBubble
        key={message.id}
        isOwn={isOwn}
        initial={{ opacity: 0, x: isOwn ? 20 : -20, scale: 0.8 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
      >
        {!isOwn && (
          <MessageHeader>
            {message.sender.name}
          </MessageHeader>
        )}
        <MessageContent>
          {message.text}
        </MessageContent>
        <MessageTime isOwn={isOwn}>
          {formatTime(message.timestamp)}
        </MessageTime>
      </MessageBubble>
    );
  };

  return (
    <ChatContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <ChatHeader>
        <div>
          <ChatTitle>💬 Chat</ChatTitle>
          <OnlineIndicator>
            <OnlineDot />
            {participants.length} online
          </OnlineIndicator>
        </div>
        <CloseButton
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          ✕
        </CloseButton>
      </ChatHeader>

      <MessagesContainer>
        <AnimatePresence>
          {messages.map(message => renderMessage(message))}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <InputWrapper>
          <MessageInput
            ref={inputRef}
            placeholder="Scrivi un messaggio..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={1}
          />

          <SendButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🚀
          </SendButton>
        </InputWrapper>
      </InputContainer>
    </ChatContainer>
  );
}

export default ChatPanel;